import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitApplication, fileToBase64 } from '../../../services/jobApplicationService';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const ROLE_ID = 'estate-handyman-caretaker';
const ROLE_TITLE = 'Estate Handyman & Holiday Let Caretaker — Land & Property';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: gap 0.2s ease;

  &:hover { gap: 0.7rem; }
`;

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border-top: 4px solid ${props => props.theme.colors.accent};

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const JobTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const JobSubtitle = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 1.5rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 160px;
  max-width: 320px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: ${props => props.$highlight
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.07)'};
  color: ${props => props.$highlight ? 'white' : props.theme.colors.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(16,29,64,0.08);
`;

const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;

  &:last-child { margin-bottom: 0; }
`;

const SubHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  margin: 1.5rem 0 0.6rem;

  &:first-of-type { margin-top: 0; }
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const StickyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border-top: 4px solid ${props => props.theme.colors.primary};
  position: sticky;
  top: 140px;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.92rem;
  margin-bottom: 0.4rem;

  span.req { color: ${props => props.theme.colors.accent}; }
`;

const fieldStyles = `
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  background: white;

  &::placeholder { color: #aaa; }
`;

const Input = styled.input`
  ${fieldStyles}

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const Select = styled.select`
  ${fieldStyles}

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  resize: vertical;
  min-height: 100px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FileUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.$hasFile
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.2)'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$hasFile
    ? 'rgba(16,29,64,0.04)'
    : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(16,29,64,0.03);
  }

  input { display: none; }
`;

const FileUploadText = styled.div`
  font-size: 0.88rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.$selected ? '600' : '500'};
  opacity: ${props => props.$selected ? 1 : 0.6};
`;

const FileUploadHint = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  margin-top: 0.35rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16,29,64,0.08);
  margin: 1.25rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0.5rem 0 0;
  text-align: center;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 2rem 0;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const SuccessTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
  line-height: 1.6;
  margin: 0;
`;

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  rate: '',
  startDate: '',
  insurance: '',
  customerExample: '',
};

const EstateHandymanCaretaker = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (setter) => (e) => {
    setter(e.target.files[0] || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!cvFile) {
      setError('Please attach your CV.');
      return;
    }
    if (!coverFile) {
      setError('Please attach a cover letter or a brief summary of your experience.');
      return;
    }

    const coverNote = [
      `Rate expectations: ${form.rate.trim()}`,
      `Available to start: ${form.startDate.trim()}`,
      `Public liability insurance: ${form.insurance}`,
      '',
      'An example of going out of my way to help a customer or guest:',
      form.customerExample.trim(),
    ].join('\n');

    setSubmitting(true);
    try {
      const [cvData, coverData] = await Promise.all([
        fileToBase64(cvFile),
        fileToBase64(coverFile),
      ]);

      await submitApplication({
        roleId: ROLE_ID,
        roleTitle: ROLE_TITLE,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        coverNote,
        cvData,
        cvFileName: cvFile.name,
        coverLetterData: coverData,
        coverLetterFileName: coverFile.name,
      });

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        title="Estate Handyman & Holiday Let Caretaker"
        subtitle="Land & Property Department · Belvoir Estate"
        image={DAY_TICKETS_BANNER_CDN}
      />

      <ContentContainer>
        <BackLink to="/about/careers">← Back to Careers</BackLink>

        <JobHeader>
          <JobTitle>Estate Handyman &amp; Holiday Let Caretaker</JobTitle>
          <JobSubtitle>Land &amp; Property — Belvoir Estate, Leicestershire</JobSubtitle>
          <MetaRow>
            <MetaItem>
              <MetaLabel>Rate</MetaLabel>
              <MetaValue>£15 – £25 per hour, depending on skills and experience, invoiced per job</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Reports To</MetaLabel>
              <MetaValue>Honor Loxton, Property Maintenance &amp; Compliance Coordinator</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Engagement</MetaLabel>
              <MetaValue>Self-employed contractor (no employment relationship; invoiced per job)</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Hours</MetaLabel>
              <MetaValue>Approximately 10 – 40 hours per week as work is issued, including weekends</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>Belvoir Estate, Leicestershire (own transport essential)</MetaValue>
            </MetaItem>
          </MetaRow>
          <TagRow>
            <Tag $highlight>Now Hiring</Tag>
            <Tag>Property &amp; Land</Tag>
            <Tag>Holiday Lets</Tag>
            <Tag>Maintenance</Tag>
            <Tag>Contractor</Tag>
          </TagRow>
        </JobHeader>

        <Grid>
          <div>
            <Section>
              <SectionTitle>The Opportunity</SectionTitle>
              <BodyText>
                Belvoir Estate is seeking a skilled, personable all-rounder to work with the Land &amp; Property
                team as a contractor, providing handyman and holiday let caretaker services. You will keep our let
                properties in good order and, at weekends and out of hours, act as the first point of contact for
                guests staying in our holiday cottages.
              </BodyText>
              <BodyText>
                The role suits someone who takes pride in practical work, genuinely enjoys helping people, and is
                at their best when a problem needs solving quickly, calmly and with a smile.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>The Role</SectionTitle>

              <SubHeading>Holiday let caretaker: weekends and out-of-hours</SubHeading>
              <BodyText>
                Guests choose Belvoir for a memorable stay, and how a problem is handled can define it. You will
                respond to call-outs promptly and courteously, covering everyday issues such as appliance faults,
                leaks and blocked drains, tripped circuits and lighting, alarms, lockouts, and TV, Wi-Fi and
                heating problems. You will resolve what you reasonably can, and make safe and escalate the rest.
                Qualified plumbers and electricians are on standby as back-up, so you will not be expected to
                tackle anything highly technical.
              </BodyText>

              <SubHeading>Estate handyman: let portfolio</SubHeading>
              <BodyText>
                During the week you will carry out jobs issued by the Maintenance &amp; Compliance Coordinator:
              </BodyText>
              <BulletList>
                <li><strong>Light joinery:</strong> easing and re-hanging doors, locks, handles, skirtings, shelves and floorboards.</li>
                <li>Painting and decorating, patch repairs, filling, basic plastering, and re-sealing or re-grouting kitchens and bathrooms.</li>
                <li><strong>Minor plumbing and electrical:</strong> taps, cisterns, blockages, like-for-like sockets, switches and light fittings, and resetting trips.</li>
                <li><strong>Gardens and exteriors:</strong> mowing, pruning, clearance, fences, gates, gutters, drains and minor groundworks.</li>
                <li>Assembling and fitting furniture, removing small items, and remedial works between tenancies.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>How the Work Is Managed</SectionTitle>
              <BulletList>
                <li>You will be issued a job sheet for each job. Return it marked complete, with photos where requested, together with an invoice for that job. Once the job is marked off, the invoice is paid.</li>
                <li>Gas, fixed-wiring and structural work are outside the role. Make safe, report, and call in the standby specialists.</li>
                <li>Materials are supplied by the Estate or reimbursed at cost. Work safely, and tell us promptly if a job is larger than expected.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>About You</SectionTitle>

              <SubHeading>Essential</SubHeading>
              <BulletList>
                <li>A naturally guest- and customer-facing manner. Warm, positive and eager to help, with real pride in leaving people with a good impression of the Estate. This matters more to us than anything else on this list.</li>
                <li>Proven experience in general maintenance, handyman work or a building trade, with practical skills across a wide range of small repairs.</li>
                <li>Sound judgement: able to diagnose a problem quickly, and to know when to make safe and call for specialist help.</li>
                <li>Willing and able to cover weekends and respond to out-of-hours call-outs. This is not negotiable.</li>
                <li>Reliable, calm under pressure, well organised and comfortable working alone.</li>
                <li>Full UK driving licence, own vehicle and tools, and public liability insurance (required). You will be responsible for your own tax, National Insurance and insurance as a self-employed contractor.</li>
                <li>Discretion and trustworthiness when working in tenants’ homes and guests’ holiday accommodation.</li>
              </BulletList>

              <SubHeading>Desirable</SubHeading>
              <BulletList>
                <li>Recognised trade qualifications, or experience in holiday lets, hospitality or estate management.</li>
                <li>Ride-on mower or chainsaw certificates; first aid certificate.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>What We Offer</SectionTitle>
              <BulletList>
                <li>Regular, varied work of up to around 40 hours a week, at a rate that reflects your skills and experience, with prompt payment once each job is marked off.</li>
                <li>Varied work across one of the region’s best-known rural estates.</li>
                <li>Clear job sheets, and qualified specialists on hand when work needs more than a handyman.</li>
                <li>A supportive property team who value reliability and a positive attitude, and a long-term working relationship for the right contractor.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>About Belvoir Estate</SectionTitle>
              <BodyText>
                Belvoir Estate is a large, mixed-use rural estate in Leicestershire with around 500 tenancies across
                residential, commercial, agricultural, renewables, hospitality, leisure and events, including a
                portfolio of holiday cottages. The Land &amp; Property department is growing, and this role is
                central to how we look after our tenants and guests.
              </BodyText>
            </Section>
          </div>

          <StickyCard>
            {submitted ? (
              <SuccessWrap>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessTitle>Application Received</SuccessTitle>
                <SuccessText>
                  Thank you for your application. We'll be in touch if your experience matches what we're looking for.
                </SuccessText>
              </SuccessWrap>
            ) : (
              <>
                <CardTitle>Apply for This Role</CardTitle>
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label htmlFor="ehc-first-name">First Name <span className="req">*</span></Label>
                    <Input id="ehc-first-name" type="text" name="firstName" value={form.firstName} onChange={handleChange} placeholder="Jane" required />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="ehc-last-name">Last Name <span className="req">*</span></Label>
                    <Input id="ehc-last-name" type="text" name="lastName" value={form.lastName} onChange={handleChange} placeholder="Smith" required />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="ehc-email">Email Address <span className="req">*</span></Label>
                    <Input id="ehc-email" type="email" name="email" value={form.email} onChange={handleChange} placeholder="jane@example.com" required />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="ehc-phone">Phone Number</Label>
                    <Input id="ehc-phone" type="tel" name="phone" value={form.phone} onChange={handleChange} placeholder="+44 7700 900000" />
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>CV <span className="req">*</span></Label>
                    <FileUploadArea $hasFile={!!cvFile}>
                      <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange(setCvFile)} />
                      <FileUploadText $selected={!!cvFile}>
                        {cvFile ? cvFile.name : 'Choose file — CV'}
                      </FileUploadText>
                      {!cvFile && <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>}
                    </FileUploadArea>
                  </FormGroup>

                  <FormGroup>
                    <Label>Cover Letter or Summary of Experience <span className="req">*</span></Label>
                    <FileUploadArea $hasFile={!!coverFile}>
                      <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange(setCoverFile)} />
                      <FileUploadText $selected={!!coverFile}>
                        {coverFile ? coverFile.name : 'Choose file — Cover Letter / Summary'}
                      </FileUploadText>
                      {!coverFile && <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>}
                    </FileUploadArea>
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label htmlFor="ehc-rate">Day Rate or Hourly Rate Expectations <span className="req">*</span></Label>
                    <Input id="ehc-rate" type="text" name="rate" value={form.rate} onChange={handleChange} placeholder="e.g. £20 per hour" required />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="ehc-start">When Could You Start? <span className="req">*</span></Label>
                    <Input id="ehc-start" type="text" name="startDate" value={form.startDate} onChange={handleChange} placeholder="e.g. Immediately, or 1 December" required />
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="ehc-insurance">Do You Hold Public Liability Insurance? <span className="req">*</span></Label>
                    <Select id="ehc-insurance" name="insurance" value={form.insurance} onChange={handleChange} required>
                      <option value="">Please select</option>
                      <option value="Yes">Yes</option>
                      <option value="No, but I can arrange it">No, but I can arrange it</option>
                      <option value="No">No</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label htmlFor="ehc-example">An Example of Helping a Customer or Guest <span className="req">*</span></Label>
                    <TextArea
                      id="ehc-example"
                      name="customerExample"
                      value={form.customerExample}
                      onChange={handleChange}
                      placeholder="In a few lines, tell us about a time you went out of your way to help a customer or guest."
                      rows={5}
                      required
                    />
                  </FormGroup>

                  {error && <StatusMessage>{error}</StatusMessage>}

                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </SubmitButton>

                  <SmallNote style={{ marginTop: '0.85rem' }}>
                    Shortlisted candidates will be asked for references, proof of right to work, proof of public
                    liability insurance and a driving licence check. Belvoir Estate is an equal opportunities business.
                  </SmallNote>
                </form>
              </>
            )}
          </StickyCard>
        </Grid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default EstateHandymanCaretaker;
